<template>
    <div class="settings">
        <h1>{{$t('title')}}
            <span v-for="(p, ind) in parents" :key="ind">
                <template v-if="ind!==0">/</template><span :class="{folderSelect: ind !== parents.length -1}"
                    @click="selectFolder(p.id)">{{p.name}}</span></span>
        </h1>
        <h2>{{$t('stats')}}</h2>
        <div>{{$t('messageCount', { count: folder.msgs, unread: folder.unread, size: folder.size })}}</div>
        <b-button size="sm" v-if="canDelete" @click="doDeleteFolder">{{$t('delete')}}</b-button>
        <b-modal ref="delete-modal" :title="$t('deleteProgressTitle')" no-close-on-backdrop hide-header-close
                no-close-on-esc>
            {{deleteProgress}}
            <template v-slot:modal-footer>
                <b-button v-if="deleteFinished" @click="$refs['delete-modal'].hide()">{{$t('closeDelete')}}</b-button>
                <b-button v-else @click="cancelDelete = true">{{$t('cancelDelete')}}</b-button>
            </template>
        </b-modal>
        <b-button size="sm" v-if="canRename" @click="openRenameDialog">{{$t('rename')}}</b-button>
        <b-modal ref="rename-modal" :title="$t('renameDialogTitle')"
                @ok="doRenameFolder" :ok-disabled="renameDialogInvalid">
            <RenameDialog ref="rename-dialog" :moveFolder="folder.name" @input="onRenameDialogInput"></RenameDialog>
        </b-modal>
        <div>
            <b-checkbox size="sm" switch v-model="isFavourite" class="d-inline-block hasptr">
                {{$t('favouriteFolder')}}
            </b-checkbox>
            <span v-if="isFavourite" class="favouriteMover">
                <font-awesome-icon size="sm" icon="arrow-alt-up" @click="doFavouriteUp"
                        :class="{disabled: favouriteAtTop}"></font-awesome-icon>
                <font-awesome-icon size="sm" icon="arrow-alt-down" @click="doFavouriteDown"
                        :class="{disabled: favouriteAtBottom}"></font-awesome-icon>
            </span>
        </div>
        <div v-if="canHaveChildren" class="childWrap">
            <h2>{{this.$t('childrenTitle')}}</h2>
            <b-container class="children">
                <b-row v-for="c in folder.children" :key="c.id" class='child folderSelect' @click="selectFolder(c.id)">
                    <b-col class="childName">{{$t('childName', {name: c.dispName})}}</b-col>
                    <b-col class="childMsgs" cols="3">{{$t('childMessages', {count: c.msgs})}}</b-col>
                    <b-col class="childSize" cols="2">{{$t('childSize', {size: c.size})}}</b-col>
                </b-row>
                <b-row v-if="folder.children.length === 0">
                    <b-col>{{$t('noChildren')}}</b-col>
                </b-row>
            </b-container>
            <b-button variant="primary" size="sm" @click="doAddChild">{{$t('addChild')}}</b-button>
        </div>
        <div v-if="canSetACL && aclLoaded" class="aclWrap">
            <div class="aclUserTable">
                <h2>{{$t('acl.title')}}</h2>
                <list :entries="acl" @select="selectACL" @change="selectACL" ref="aclList">
                    <template v-slot:entry="{ entryIndex, entryValue }">
                        <div class="aclname">{{getACLName(entryValue)}}</div>
                        <div class="acltype">{{getACLType(entryValue)}}</div>
                    </template>
                </list>
                <b-row>
                    <b-col>
                        <b-select v-model="newACL" :options="availableAclUsers"></b-select>
                    </b-col>
                    <b-col>
                        <b-button variant="primary" size="sm" @click="addACL" :disabled="!newACL">{{$t('addPermission')
                            }}</b-button>
                    </b-col>
                </b-row>
            </div>
            <acl-editor :acl="aclEntry" v-if="aclEntry" @save="saveACL" :state="aclEditorState"
                    @delete="deleteACL"
                    :disabled="aclEntry.name===this.$store.state.username"></acl-editor>
        </div>
    </div>
</template>

<script>
import { imapDecode, imapEncode } from 'emailjs-utf7';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RenameDialog from '@/components/renameDialog';
import List from '@/components/list';
import AclEditor from '@/components/aclEditor';

export default {
  name: 'folderConfig',
  components: { RenameDialog, FontAwesomeIcon, List, AclEditor },
  i18nOptions: { keyPrefix: 'folderConfig' },
  data: function() {
    return {
      aclLoaded: false,
      userlist: [],
      acl: [],
      newACL: null,
      deleteProgress: '',
      deleteError: false,
      cancelDelete: false,
      deleteFinished: false,
      renameName: '',
      renameDialogInvalid: true,
      aclEntry: null,
      aclEditorState: ''
    };
  },
  props: {
    folderid: String
  },
  watch: {
    folderid() {
      this.loadACL();
    }
  },
  mounted() {
    this.loadACL();
  },
  methods: {
    loadACL() {
      this.aclLoaded = false;
      if (this.canSetACL) {
        axios.get('rest/user/settings/userlist')
          .then(d => {
            console.log('got Users', d);
            this.userlist = d.data;
            axios.get(`rest/user/folders/${this.folderId}/acl`)
              .then(d => {
                console.log('got ACL', d);
                this.acl = d.data;
                this.sortACL();
                this.aclLoaded = true;
              });
          });
      }
    },
    saveACL(val) {
      this.$set(this.acl, this.aclEntryIndex, val);
      this.aclEditorState = 'saving';
      axios.put(`rest/user/folders/${this.folderId}/acl`, { acl: this.acl })
        .then(d => {
          this.acl = d.data;
          this.sortACL();
        })
        .catch(e => {
          this.$dialog.alert(this.$t('aclSaveError', { error: e.message }));
        })
        .finally(() => {
          this.aclEditorState = '';
        });
    },
    deleteACL(val) {
      this.$delete(this.acl, this.aclEntryIndex);
      this.aclEditorState = 'deleting';
      axios.put(`rest/user/folders/${this.folderId}/acl`, { acl: this.acl })
        .then(d => {
          this.acl = d.data;
          this.sortACL();
        })
        .catch(e => {
          this.$dialog.alert(this.$t('aclDeleteError', { error: e.message }));
        })
        .finally(() => {
          this.aclEditorState = '';
        });
    },
    selectACL({ index, entry }) {
      console.log('select ACL', entry);
      this.aclEntryIndex = index;
      this.aclEntry = entry;
    },
    addACL() {
      this.acl.push(Object.assign({}, this.newACL, { rights: 'lr' }));
      this.sortACL();
      this.aclEditorState = 'adding';
      axios.put(`rest/user/folders/${this.folderId}/acl`, { acl: this.acl })
        .then(d => {
          this.acl = d.data;
          this.sortACL();
        })
        .catch(e => {
          this.$dialog.alert(this.$t('aclAddError', { error: e.message }));
        })
        .finally(() => {
          this.aclEditorState = '';
        });
      this.$nextTick(() => {
        this.$refs.aclList.selectIndex(this.acl.findIndex(a => a.name === this.newACL.name && a.type ===
          this.newACL.type));
        this.newACL = null;
      });
    },
    sortACL() {
      this.acl = this.acl.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    getACLName(acl) {
      if (acl.type === 'builtin') {
        return this.$t(`acl.builtin.${acl.name}`);
      } else {
        return acl.name;
      }
    },
    getACLType(acl) {
      return this.$t(`acl.type.${acl.type}`);
    },
    selectFolder(id) {
      this.$router.push(`/n/${id}/config`);
    },
    doDeleteFolder() {
      const TrashFolderName = this.$store.state.specialFolders['\\Trash'];
      let TrashFolder = null;
      const parentFolderName = this.folder.name.substring(0, this.folder.name.lastIndexOf('/'));
      let successPath;
      if (parentFolderName === '') {
        successPath = `/n/${this.$store.getters.getFolderByName('Inbox')}/1`;
      } else {
        successPath = `/n/${this.$store.getters.getFolderByName(parentFolderName).id}/config`;
      }
      if (TrashFolderName) {
        TrashFolder = this.$store.getters.getFolderByName(TrashFolderName);
        console.log('Trash Folder', TrashFolder);
        if (TrashFolder) {
          if (this.folder.name.substr(0, TrashFolderName.length + 1) === `${TrashFolderName}/`) {
            this.$dialog.confirm(this.$t('permanentDeleteConfirm', { folder: imapDecode(this.folder.name) }), {
              type: 'hard',
              verification: this.$t('permanentDeleteVerification'),
              verificationHelp: this.$t('permanentDeleteVerificationPrompt')
            })
              .then(d => {
                const folderList = this.getFoldersToDelete(this.folder);
                console.log('folderList', folderList);
                this.cancelDelete = false;
                this.deleteFinished = false;
                this.deleteError = false;
                this.deleteProgress = '';
                this.$refs['delete-modal'].show();
                (async () => {
                  while (!this.cancelDelete) {
                    if (!folderList.length) {
                      break;
                    }
                    const f = folderList.shift();
                    console.log('delete folder', f.id);
                    this.deleteProgress = this.$t('permanentDeletingFolder', { folder: f.name });
                    let promise = new Promise((resolve, reject) => {
                      setTimeout(() => resolve(), 100);
                    });
                    await promise;
                    await axios.delete(`/rest/user/folders/${f.id}`)
                      .then(d => {
                        console.log('delete result', d);
                        if (d.data.success) {
                          // this.$store.dispatch('getFolders');
                          // this.$router.replace(successPath);
                        } else {
                          this.deleteProgress = this.$t('permanentDeleteError', {
                            error: this.$t(`general:storeErrors.${d.data.error}`) });
                          this.deleteError = true;
                          this.cancelDelete = true;
                        }
                      })
                      .catch(e => {
                        this.$dialog.alert(this.$t('permanentDeleteError', { error: e.message }));
                      });
                  }
                  this.deleteFinished = true;
                  this.$store.dispatch('getFolders');
                  if (this.cancelDelete) {
                    if (!this.deleteError) {
                      this.deleteProgress = this.$t('cancelledDelete');
                    }
                  } else {
                    this.deleteProgress = this.$t('finishedDelete');
                    this.$router.replace(successPath);
                  }
                })();
              });
          } else {
            this.$dialog.confirm(this.$t('deleteConfirm', { folder: imapDecode(this.folder.name) }), {
              type: 'hard',
              verification: this.$t('deleteVerification'),
              verificationHelp: this.$t('deleteVerificationPrompt')
            })
              .then(d => {
                const origNewFolder = `${TrashFolder.name}/${imapEncode(this.folder.dispName)}`;
                let newFolder = origNewFolder;
                let found = false;
                let suffix = 1;
                do {
                  if (this.$store.getters.getFolderByName(newFolder)) {
                    newFolder = `${origNewFolder}_${suffix++}`;
                    found = true;
                  } else {
                    found = false;
                  }
                } while (found);
                this.$RPC(`folders/${this.folderId}`, 'rename', { newFolderName: newFolder })
                  .then(d => {
                    console.log('rename result', d);
                    if (d.success) {
                      this.$store.dispatch('getFolders');
                      this.$router.replace(successPath);
                    } else {
                      this.$dialog.alert(this.$t('deleteError', {
                        error: this.$t(`general:storeErrors.${d.error}`)
                      }));
                    }
                  })
                  .catch(e => {
                    this.$dialog.alert(this.$t('deleteError', { error: e.message }));
                  });
              });
          }
        }
      }
    },
    getFoldersToDelete(folder) {
      let folderList = [];
      for (let c in folder.children) {
        folderList = [...folderList, ...this.getFoldersToDelete(folder.children[c])];
      }
      folderList.push(folder);
      return folderList;
    },
    openRenameDialog() {
      this.$refs['rename-modal'].show();
    },
    onRenameDialogInput({ name, invalid }) {
      console.log('rd input', name, invalid);
      this.renameName = name;
      this.renameDialogInvalid = invalid;
    },
    doRenameFolder() {
      alert('rename ' + this.renameName);
      this.$RPC(`folders/${this.folderId}`, 'rename', { newFolderName: this.renameName })
        .then(d => {
          console.log('rename result', d);
          if (d.success) {
            this.$store.dispatch('getFolders');
          } else {
            this.$dialog.alert(this.$t('renameError', {
              error: this.$t(`general:storeErrors.${d.error}`)
            }));
          }
        })
        .catch(e => {
          this.$dialog.alert(this.$t('renameError', { error: e.message }));
        });
    },
    doAddChild() {
      this.$dialog.prompt({
        title: this.$t('addChildTitle'),
        body: this.$t('addChildText')
      }, {
        promptHelp: this.$t('addChildPrompt')
      })
        .then(d => {
          if (d.data) {
            axios.post(`rest/user/folders/${this.folderId}`, { newfolder: d.data })
              .then(d => {
                console.log('add Folder', d);
                if (d.data.success) {
                  this.$store.dispatch('getFolders');
                } else {
                  this.$dialog.alert(this.$t('addChildError', {
                    error: this.$t(`general:storeErrors.${d.data.error}`)
                  }));
                }
              })
              .catch(e => {
                this.$dialog.alert(this.$t('addChildError', { error: e.message }));
              });
          }
        });
    },
    doFavouriteUp() {
      this.$store.dispatch('moveFavouriteFolderUp', { name: this.folder.name });
    },
    doFavouriteDown() {
      this.$store.dispatch('moveFavouriteFolderDown', { name: this.folder.name });
    }
  },
  computed: {
    canDelete() {
      if ((this.folder.name.toUpperCase() === 'INBOX') || (this.folder.specialUse.length > 0)) {
        return false;
      } else {
        return this.folder.rights.indexOf('x') !== -1;
      }
    },
    canRename() {
      return this.canDelete;// && (Object.keys(this.folder.children).length === 0);
    },
    canHaveChildren() {
      return this.folder.rights.indexOf('k') !== -1;
    },
    canSetACL() {
      if (!this.$store.state.settings.enterprise) {
        return false;
      }

      return this.folder.rights.indexOf('a') !== -1;
    },
    folderId() {
      return parseInt(this.folderid, 10);
    },
    folder() {
      console.log('folder', this.$store.getters.getFolderById(this.folderId));
      return this.$store.getters.getFolderById(this.folderId);
    },
    parents() {
      const parts = this.folder.name.split('/');
      let parent = '';
      let p = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) {
          parent += '/';
        }
        parent += parts[i];
        p.push({ name: imapDecode(parts[i]), id: this.$store.getters.getFolderByName(parent).id });
      }
      console.log(p);
      return p;
    },
    isFavourite: {
      get() {
        return this.$store.getters.favouriteFolders.indexOf(this.folder.name) !== -1;
      },
      set(val) {
        console.log('set Favourite', val);
        this.$store.dispatch('setFavouriteFolder', { name: this.folder.name, value: val });
      }
    },
    favouriteAtTop() {
      return this.$store.getters.favouriteFolders.indexOf(this.folder.name) === 0;
    },
    favouriteAtBottom() {
      return this.$store.getters.favouriteFolders.indexOf(this.folder.name) ===
        this.$store.getters.favouriteFolders.length - 1;
    },
    builtins() {
      return ['admins', 'everyone', 'anyone'];
    },
    availableAclUsers() {
      return [
        { value: null, text: this.$t('acl.userSelectPlaceholder') },
        { label: this.$t('acl.new.users'),
          options: this.userlist.users
            .filter(u => this.acl.findIndex(a => a.type === 'user' && a.name === u) === -1)
            .map(u => {
              return { value: { type: 'user', name: u }, text: u };
            })
        },
        {
          label: this.$t('acl.new.groups'),
          options: this.userlist.groups
            .filter(g => this.acl.findIndex(a => a.type === 'group' && a.name === g) === -1)
            .map(g => {
              return { value: { type: 'group', name: g }, text: g };
            })
        },
        {
          label: this.$t('acl.new.builtins'),
          options: this.builtins
            .filter(g => this.acl.findIndex(a => a.type === 'builtin' && a.name === g) === -1)
            .map(g => {
              return { value: { type: 'builtin', name: g }, text: this.$t(`acl.builtin.${g}`) };
            })
        }

      ];
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .folderSelect {
        cursor: pointer;
    }

    .favouriteMover {
        margin-left: 10px;
        color: blue;
        cursor: pointer;
        .disabled {
            color: #ccc;
            cursor: default;
        }
    }
    .aclWrap {
        margin: 10px;
        .aclUserTable {
            border: 1px solid $gray-800;
            border-radius: $acl-userlist-borderradius;
            h2 {
                background-color: $dark;
                color: $light;
                padding: 5px;
                margin: 0;
            }
            button {
                margin: 10px;
            }
            ::v-deep .entry {
                display: flex;
                .aclname {
                    flex: 1;
                }
                .acltype {
                    color: $gray-600;
                    font-style: italic;
                    flex: 1;
                }
            }
        }
    }
    .childWrap {
        margin: 10px;
        border: 1px solid $gray-800;
        border-radius: $folder-child-borderradius;
        h2 {
            background-color: $dark;
            color: $light;
            padding: 5px;
            margin: 0;
        }
        button {
            margin: 10px;
        }
        .children {
            max-height: 300px;
            overflow-y: auto;
            border-bottom: 1px solid $gray-300;
            background-color: $gray-100;
            .child {
                .childName {
                    font-weight: bold;
                }
                .childSize {
                    text-align: right;
                    margin-right: 20px;
                }
                .childMsgs {
                    text-align: right;
                }
            }
        }
    }

</style>
